import { useEffect, useState, useRef } from "react";
import { Trash2, Pencil, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { api, imgUrl, isVideo, formatApiErrorDetail, SERVICES_LIST } from "../lib/api";
import { MEDIA_ACCEPT } from "./GalleryManager";

const Media = ({ src, className, controls = false }) =>
  isVideo(src)
    ? <video src={imgUrl(src)} controls={controls} muted playsInline className={className} />
    : <img src={imgUrl(src)} alt="" className={className} />;

const EMPTY = { title: "", service: "Remodeling", description: "", location: "", before_image: "", after_image: "", published: true };

export default function BeforeAfterManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = () => api.get("/admin/before_after").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file, key) => {
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        onUploadProgress: (e) => { if (e.total) toast.loading(`Uploading… ${Math.round((e.loaded / e.total) * 100)}%`, { id: "upload-progress" }); },
      });
      toast.success("Upload complete", { id: "upload-progress" });
      setEditing((e) => ({ ...e, [key]: data.urls[0] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed", { id: "upload-progress" });
    }
  };

  const save = async () => {
    if (!editing.before_image || !editing.after_image || !editing.title) { toast.error("Title, before and after media are required"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/before_after/${editing.id}`, editing);
      else await api.post("/admin/before_after", editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Before & After project? This cannot be undone.")) return;
    await api.delete(`/admin/before_after/${id}`); toast.success("Deleted"); load(); };
  const togglePublish = async (p) => { await api.put(`/admin/before_after/${p.id}`, { ...p, published: !p.published }); load(); };

  return (
    <div data-testid="portal-ba-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl">Manage Before & After</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="ba-add-btn">Add Project</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`ba-admin-item-${p.id}`}>
            {isVideo(p.before_image) || isVideo(p.after_image) ? (
              <div className="grid grid-cols-2 gap-1 bg-black" style={{ height: 220 }} data-testid={`ba-admin-media-${p.id}`}>
                <div className="relative"><Media src={p.before_image} className="w-full h-full object-cover" /><span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] uppercase tracking-wider px-2 py-0.5">Before</span></div>
                <div className="relative"><Media src={p.after_image} className="w-full h-full object-cover" /><span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] uppercase tracking-wider px-2 py-0.5">After</span></div>
              </div>
            ) : (
              <ReactCompareSlider
                itemOne={<ReactCompareSliderImage src={imgUrl(p.before_image)} alt="before" />}
                itemTwo={<ReactCompareSliderImage src={imgUrl(p.after_image)} alt="after" />}
                style={{ height: 220 }}
              />
            )}
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-[#0B1B3D]">{p.title} {!p.published && <span className="text-xs text-slate-400">(unpublished)</span>}</p>
                <p className="text-xs text-slate-400">{p.service}{p.location ? ` • ${p.location}` : ""}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => togglePublish(p)} className="p-2 border border-slate-200 text-slate-500" data-testid={`ba-publish-${p.id}`}>{p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => setEditing(p)} className="p-2 border border-slate-200 text-slate-500" data-testid={`ba-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="p-2 border border-slate-200 text-slate-400 hover:text-red-500" data-testid={`ba-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ba-edit-modal">
          <div className="bg-white w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-bold text-[#0B1B3D] text-xl">{editing.id ? "Edit Project" : "Add Project"}</h2>
              <button onClick={() => setEditing(null)} data-testid="ba-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-5">
              {[["before_image", "Before", beforeRef], ["after_image", "After", afterRef]].map(([key, label, ref]) => (
                <div key={key}>
                  <label className="field-label">{label} Photo / Video</label>
                  {editing[key] && <Media src={editing[key]} controls className="w-full h-28 object-cover mb-2 border border-slate-200" />}
                  <button onClick={() => ref.current?.click()} className="w-full border-2 border-dashed border-slate-300 py-4 text-slate-500 text-xs hover:border-[#005AE0]" data-testid={`ba-upload-${key}`}>
                    Upload {label}
                  </button>
                  <input ref={ref} type="file" accept={MEDIA_ACCEPT} className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], key)} />
                </div>
              ))}
            </div>
            <label className="field-label">Project Name</label>
            <input className="field mb-4" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="ba-title-input" />
            <label className="field-label">Service</label>
            <select className="field mb-4" value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })} data-testid="ba-service-select">
              {SERVICES_LIST.map((s) => <option key={s}>{s}</option>)}
            </select>
            <label className="field-label">Location (optional)</label>
            <input className="field mb-4" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="ba-location-input" />
            <label className="field-label">Description</label>
            <textarea className="field mb-4" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="ba-description-input" />
            <label className="flex items-center gap-2 text-sm text-slate-600 mb-6">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="ba-published-checkbox" /> Published
            </label>
            <button onClick={save} disabled={busy} className="btn-primary w-full" data-testid="ba-save-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
